import React, { useState, useEffect } from 'react';
import AdminLayout from '@/components/admin/AdminLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Plus, CheckCircle, XCircle, Clock, Calendar, Stethoscope, Shield, MessageSquare, Send, Paperclip, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '@/utils/api';
import axios from 'axios';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';

const AdminLeaves = ({
 auth, onLogout }) => {
  const [activeTab, setActiveTab] = useState('demandes');
  const [leaves, setLeaves] = useState([]);
  const [comms, setComms] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [balances, setBalances] = useState({});
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [replyCommId, setReplyCommId] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [formData, setFormData] = useState({ employee_id: '', leave_type: 'congé', start_date: '', end_date: '', reason: '' });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const leavesRes = await axios.get(`${API}/leaves`);
      setLeaves(leavesRes.data);
    } catch (err) { console.error('Erreur leaves:', err); }
    let empList = [];
    try {
      const employeesRes = await axios.get(`${API}/employees`);
      empList = employeesRes.data.filter(e => e.is_active !== false);
      setEmployees(empList);
    } catch (err) { console.error('Erreur employees:', err); }
    try {
      const commsRes = await axios.get(`${API}/communications`);
      setComms(commsRes.data);
    } catch (err) { console.error('Erreur communications:', err); }
    // Load balances for all employees
    const balMap = {};
    for (const emp of empList) {
      balMap[emp.id] = emp.leave_balance ?? 20;
    }
    setBalances(balMap);
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.employee_id || !formData.start_date || !formData.end_date || !formData.reason) {
      toast.error('Remplissez tous les champs'); return;
    }
    try {
      const emp = employees.find(e => e.id === formData.employee_id);
      await axios.post(`${API}/leaves`, { ...formData, employee_name: emp ? `${emp.first_name} ${emp.last_name}` : formData.employee_id });
      toast.success('Demande creee');
      setShowDialog(false);
      setFormData({ employee_id: '', leave_type: 'congé', start_date: '', end_date: '', reason: '' });
      loadData();
    } catch { toast.error('Erreur'); }
  };

  const [imputeLeaveId, setImputeLeaveId] = useState(null);
  const [imputeType, setImputeType] = useState('');

  const handleStatusUpdate = async (leaveId, newStatus, absenceType) => {
    try {
      const payload = { status: newStatus };
      if (newStatus === 'approved' && absenceType) {
        payload.leave_type = absenceType;
      }
      await axios.put(`${API}/leaves/${leaveId}`, payload);
      toast.success(newStatus === 'approved' ? 'Demande approuvee' : 'Demande refusee');
      setImputeLeaveId(null);
      setImputeType('');
      loadData();
    } catch { toast.error('Erreur'); }
  };

  const handleReply = async (commId) => {
    if (!replyText.trim()) return;
    try {
      await axios.post(`${API}/communications/${commId}/reply`, { message: replyText, sender: 'admin' });
      toast.success('Reponse envoyee');
      setReplyCommId(null);
      setReplyText('');
      loadData();
    } catch { toast.error('Erreur'); }
  };

  const getName = (id) => {
    const emp = employees.find(e => e.id === id);
    return emp ? `${emp.first_name} ${emp.last_name}` : id;
  };

  const pendingLeaves = leaves.filter(l => l.status === 'pending');
  const processedLeaves = leaves.filter(l => l.status !== 'pending');

  const TYPE_BADGE = {
    'congé': 'bg-purple-100 text-purple-700', vacances: 'bg-blue-100 text-blue-700',
    recup: 'bg-violet-100 text-violet-700', recuperation: 'bg-violet-100 text-violet-700',
    maladie: 'bg-red-100 text-red-700', accident: 'bg-orange-100 text-orange-700',
    non_paye: 'bg-slate-100 text-slate-700', autre: 'bg-slate-100 text-slate-700',
  };
  const COMM_ICON = { maladie: Stethoscope, accident: Shield, autre: MessageSquare };

  return (
    <AdminLayout auth={auth} onLogout={onLogout}>
      <div className="p-4 md:p-6 max-w-4xl mx-auto space-y-5">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-slate-900" data-testid="leaves-title">Demandes & Communications</h1>
          <Dialog open={showDialog} onOpenChange={setShowDialog}>
            <DialogTrigger asChild>
              <Button size="sm" data-testid="add-leave-button"><Plus className="w-4 h-4 mr-1" /> Ajouter un conge</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Nouveau conge</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Employe *</Label>
                  <Select value={formData.employee_id} onValueChange={(v) => setFormData({ ...formData, employee_id: v })}>
                    <SelectTrigger data-testid="employee-select-leave"><SelectValue placeholder="Choisir" /></SelectTrigger>
                    <SelectContent>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Type *</Label>
                  <Select value={formData.leave_type} onValueChange={(v) => setFormData({ ...formData, leave_type: v })}>
                    <SelectTrigger data-testid="leave-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="congé">Congé</SelectItem>
                      <SelectItem value="vacances">Vacances</SelectItem>
                      <SelectItem value="recup">Congé récup</SelectItem>
                      <SelectItem value="maladie">Maladie</SelectItem>
                      <SelectItem value="accident">Accident</SelectItem>
                      <SelectItem value="non_paye">Non payé</SelectItem>
                      <SelectItem value="autre">Autre</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2"><Label>Debut *</Label><Input type="date" value={formData.start_date} onChange={(e) => setFormData({ ...formData, start_date: e.target.value })} required data-testid="start-date-input" /></div>
                  <div className="space-y-2"><Label>Fin *</Label><Input type="date" value={formData.end_date} onChange={(e) => setFormData({ ...formData, end_date: e.target.value })} required data-testid="end-date-input" /></div>
                </div>
                <div className="space-y-2"><Label>Raison *</Label><Textarea value={formData.reason} onChange={(e) => setFormData({ ...formData, reason: e.target.value })} placeholder="Motif..." rows={3} required data-testid="reason-textarea" /></div>
                <Button type="submit" className="w-full" data-testid="save-leave-button">Creer</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Tabs */}
        <div className="flex bg-slate-100 rounded-xl p-1 gap-1" data-testid="admin-leave-tabs">
          <TabBtn active={activeTab === 'demandes'} onClick={() => setActiveTab('demandes')} badge={pendingLeaves.length}>
            <Calendar className="w-4 h-4 mr-1.5" /> Demandes
          </TabBtn>
          <TabBtn active={activeTab === 'communication'} onClick={() => setActiveTab('communication')} badge={comms.filter(c => !c.replies?.length).length}>
            <MessageSquare className="w-4 h-4 mr-1.5" /> Communications
          </TabBtn>
          <TabBtn active={activeTab === 'historique'} onClick={() => setActiveTab('historique')}>
            <FileText className="w-4 h-4 mr-1.5" /> Historique
          </TabBtn>
        </div>

        {loading ? <p className="text-center text-slate-500 py-12">Chargement...</p> : (
          <>
            {/* DEMANDES TAB */}
            {activeTab === 'demandes' && (
              pendingLeaves.length === 0 ? (
                <Card className="p-8 text-center rounded-xl"><Calendar className="w-10 h-10 mx-auto mb-2 text-slate-300" /><p className="text-slate-500">Aucune demande en attente</p></Card>
              ) : (
                <div className="space-y-3" data-testid="pending-leaves-list">
                  {pendingLeaves.map(leave => (
                    <Card key={leave.id} className="p-4 rounded-xl" data-testid={`leave-card-${leave.id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className="font-bold text-slate-900">{leave.employee_name || getName(leave.employee_id)}</span>
                            <Badge className={TYPE_BADGE[leave.leave_type] || TYPE_BADGE.autre}>{leave.leave_type}</Badge>
                            <Badge className="bg-amber-100 text-amber-700"><Clock className="w-3 h-3 mr-0.5" /> En attente</Badge>
                            {balances[leave.employee_id] !== undefined && (
                              <Badge className="bg-slate-100 text-slate-600" data-testid={`balance-${leave.id}`}>
                                Solde: {balances[leave.employee_id]}j
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-slate-600">
                            {leave.start_date && format(new Date(leave.start_date + 'T00:00:00'), 'd MMM', { locale: fr })}
                            {' – '}
                            {leave.end_date && format(new Date(leave.end_date + 'T00:00:00'), 'd MMM yyyy', { locale: fr })}
                          </p>
                          <p className="text-sm text-slate-500 mt-1">{leave.reason}</p>
                        </div>
                        <div className="flex flex-col gap-2 shrink-0">
                          {imputeLeaveId === leave.id ? (
                            <div className="space-y-2 p-2 bg-emerald-50 rounded-lg border border-emerald-200" data-testid={`impute-panel-${leave.id}`}>
                              <p className="text-xs font-bold text-emerald-800">Imputer comme :</p>
                              <Select value={imputeType} onValueChange={setImputeType}>
                                <SelectTrigger className="h-8 text-xs w-40" data-testid={`impute-select-${leave.id}`}>
                                  <SelectValue placeholder="Catégorie..." />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="vacances">Vacances</SelectItem>
                                  <SelectItem value="recup">Congé récup</SelectItem>
                                  <SelectItem value="maladie">Maladie</SelectItem>
                                  <SelectItem value="accident">Accident</SelectItem>
                                  <SelectItem value="non_paye">Non payé</SelectItem>
                                  <SelectItem value="congé">Congé (autre)</SelectItem>
                                </SelectContent>
                              </Select>
                              <div className="flex gap-1">
                                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs flex-1"
                                  disabled={!imputeType}
                                  onClick={() => handleStatusUpdate(leave.id, 'approved', imputeType)}
                                  data-testid={`confirm-approve-${leave.id}`}>
                                  Valider
                                </Button>
                                <Button size="sm" variant="ghost" className="text-xs"
                                  onClick={() => { setImputeLeaveId(null); setImputeType(''); }}>
                                  Annuler
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <div className="flex gap-2">
                              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                onClick={() => { setImputeLeaveId(leave.id); setImputeType(leave.leave_type || ''); }}
                                data-testid={`approve-leave-${leave.id}`}>
                                <CheckCircle className="w-4 h-4 mr-1" /> Accepter
                              </Button>
                              <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50"
                                onClick={() => handleStatusUpdate(leave.id, 'rejected')}
                                data-testid={`reject-leave-${leave.id}`}>
                                <XCircle className="w-4 h-4 mr-1" /> Refuser
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )
            )}

            {/* COMMUNICATIONS TAB */}
            {activeTab === 'communication' && (
              comms.length === 0 ? (
                <Card className="p-8 text-center rounded-xl"><MessageSquare className="w-10 h-10 mx-auto mb-2 text-slate-300" /><p className="text-slate-500">Aucune communication</p></Card>
              ) : (
                <div className="space-y-3" data-testid="communications-list">
                  {comms.map(comm => {
                    const CommIcon = COMM_ICON[comm.comm_type] || COMM_ICON.autre;
                    return (
                      <Card key={comm.id} className="p-4 rounded-xl" data-testid={`comm-card-${comm.id}`}>
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-bold text-slate-900">{comm.employee_name || getName(comm.employee_id)}</span>
                              <Badge className={TYPE_BADGE[comm.comm_type] || TYPE_BADGE.autre}>
                                <CommIcon className="w-3 h-3 mr-0.5" /> {comm.comm_type || comm.subject}
                              </Badge>
                            </div>
                            <span className="text-xs text-slate-400">{comm.created_at && format(new Date(comm.created_at), 'd MMM HH:mm', { locale: fr })}</span>
                          </div>
                          <p className="text-sm text-slate-700 bg-slate-50 p-3 rounded-lg">{comm.message}</p>
                          {comm.file_url && (
                            <a href={`${API.replace('/api', '')}${comm.file_url}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:underline">
                              <Paperclip className="w-3.5 h-3.5" /> Voir la piece jointe
                            </a>
                          )}
                          {comm.replies?.length > 0 && (
                            <div className="space-y-2 pl-3 border-l-2 border-slate-200">
                              {comm.replies.map((r, i) => (
                                <div key={i} className={`p-2.5 rounded-lg text-sm ${r.sender === 'admin' ? 'bg-blue-50 text-blue-800' : 'bg-slate-50 text-slate-700'}`}>
                                  <span className="text-xs font-bold">{r.sender === 'admin' ? 'Vous' : comm.employee_name || 'Employe'}</span>
                                  <p className="mt-0.5">{r.message}</p>
                                </div>
                              ))}
                            </div>
                          )}
                          {replyCommId === comm.id ? (
                            <div className="flex gap-2">
                              <Input value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder="Votre reponse..." className="flex-1" data-testid={`reply-input-${comm.id}`} />
                              <Button size="sm" onClick={() => handleReply(comm.id)} data-testid={`send-reply-${comm.id}`}><Send className="w-4 h-4" /></Button>
                              <Button size="sm" variant="ghost" onClick={() => { setReplyCommId(null); setReplyText(''); }}>Annuler</Button>
                            </div>
                          ) : (
                            <Button variant="outline" size="sm" onClick={() => setReplyCommId(comm.id)} data-testid={`reply-btn-${comm.id}`}>
                              <Send className="w-3.5 h-3.5 mr-1" /> Repondre
                            </Button>
                          )}
                        </div>
                      </Card>
                    );
                  })}
                </div>
              )
            )}

            {/* HISTORIQUE TAB */}
            {activeTab === 'historique' && (
              processedLeaves.length === 0 ? (
                <Card className="p-8 text-center rounded-xl"><FileText className="w-10 h-10 mx-auto mb-2 text-slate-300" /><p className="text-slate-500">Aucun historique</p></Card>
              ) : (
                <div className="space-y-3" data-testid="leaves-history">
                  {processedLeaves.map(leave => (
                    <Card key={leave.id} className="p-4 rounded-xl" data-testid={`history-${leave.id}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-slate-900">{leave.employee_name || getName(leave.employee_id)}</span>
                            <Badge className={TYPE_BADGE[leave.leave_type] || TYPE_BADGE.autre}>{leave.leave_type}</Badge>
                            <Badge className={leave.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                              {leave.status === 'approved' ? <><CheckCircle className="w-3 h-3 mr-0.5" /> Approuve</> : <><XCircle className="w-3 h-3 mr-0.5" /> Refuse</>}
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-500">
                            {leave.start_date && format(new Date(leave.start_date + 'T00:00:00'), 'd MMM', { locale: fr })}
                            {' – '}
                            {leave.end_date && format(new Date(leave.end_date + 'T00:00:00'), 'd MMM yyyy', { locale: fr })}
                            {leave.reason && ` — ${leave.reason}`}
                          </p>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
};

const TabBtn = ({ active, onClick, badge, children }) => (
  <button
    onClick={onClick}
    className={`flex-1 py-2 px-3 text-sm font-medium rounded-lg transition-all flex items-center justify-center ${active ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
  >
    {children}
    {badge > 0 && <Badge className="ml-1.5 bg-red-100 text-red-700 text-[10px] px-1.5">{badge}</Badge>}
  </button>
);

export default AdminLeaves;
